/* =============================================
   Kaero Scroll Animations — Premium Edition
   ============================================= */
(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch        = window.matchMedia('(pointer: coarse)').matches;

  function makeObserver(cb, opts) {
    return new IntersectionObserver(cb, {
      threshold:  opts.threshold  !== undefined ? opts.threshold  : 0.12,
      rootMargin: opts.rootMargin !== undefined ? opts.rootMargin : '0px 0px -60px 0px'
    });
  }

  /* ── Generic [data-animate] observer ── */
  function initGeneric() {
    const els = document.querySelectorAll('[data-animate]');
    if (!els.length) return;
    const obs = makeObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } });
    }, {});
    els.forEach(el => obs.observe(el));
  }

  /* ── Stagger ── */
  function initStagger() {
    const containers = document.querySelectorAll('[data-stagger]');
    if (!containers.length) return;
    const obs = makeObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    containers.forEach(c => Array.from(c.children).forEach(child => obs.observe(child)));
  }

  /* ── Parallax ── */
  function initParallax() {
    const els = document.querySelectorAll('[data-parallax]');
    if (!els.length || isTouch) return;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          els.forEach(el => {
            const speed = parseFloat(el.dataset.parallax) || 0.25;
            const rect  = el.getBoundingClientRect();
            el.style.transform = 'translateY(' + ((rect.top + rect.height / 2 - window.innerHeight / 2) * speed).toFixed(1) + 'px)';
          });
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ══════════════════════════════════════════
     WOW EFFECTS
     ══════════════════════════════════════════ */

  /* ── 1. Cinematic page intro ── */
  function initCinematicIntro() {
    const cinema = document.createElement('div');
    cinema.id = 'ks-cinematic';
    cinema.innerHTML = '<div class="ks-panel-top"></div><div class="ks-panel-bot"></div>';
    document.body.prepend(cinema);
    // Trigger reveal after short delay
    requestAnimationFrame(() => {
      setTimeout(() => {
        cinema.classList.add('ks-reveal');
        setTimeout(() => cinema.remove(), 1100);
      }, 200);
    });
  }

  /* ── 2. Scroll progress bar ── */
  function initScrollProgress() {
    const bar = document.createElement('div');
    bar.id = 'kaero-scroll-progress';
    document.body.prepend(bar);
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          bar.style.width = (max > 0 ? Math.min(window.scrollY / max * 100, 100) : 0).toFixed(1) + '%';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── 3. Nav glassmorphism + active tracking ── */
  function initNavGlass() {
    const nav = document.getElementById('nav');
    if (!nav) return;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          nav.classList.toggle('ks-nav-scrolled', window.scrollY > 60);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    document.querySelectorAll('.nav-links a').forEach(a => a.setAttribute('data-ks-nav-link', ''));

    const links   = Array.from(document.querySelectorAll('.nav-links a[href^="#"]'));
    const targets = links.map(l => document.querySelector(l.getAttribute('href'))).filter(Boolean);
    if (targets.length) {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            links.forEach(l => l.classList.remove('ks-nav-active'));
            const link = links.find(l => l.getAttribute('href') === '#' + e.target.id);
            if (link) link.classList.add('ks-nav-active');
          }
        });
      }, { threshold: 0.4 });
      targets.forEach(t => obs.observe(t));
    }
  }

  /* ── 4. Animated gradient blobs in hero ── */
  function initHeroBlobs() {
    const hero = document.querySelector('.hero-left')?.closest('section')
              || document.querySelector('#hero');
    if (!hero) return;
    hero.style.position = 'relative';
    hero.style.overflow = 'hidden';
    const blobs = [
      { w: 520, h: 520, l: '-8%',  t: '-10%', color: 'rgba(91,141,239,0.22)',  dur: '9s',  tx: '80px',  ty: '60px',  sc: 1.15 },
      { w: 420, h: 420, l: '55%',  t: '30%',  color: 'rgba(155,109,255,0.18)', dur: '13s', tx: '-60px', ty: '-50px', sc: 0.88 },
      { w: 320, h: 320, l: '20%',  t: '60%',  color: 'rgba(233,64,128,0.14)',  dur: '11s', tx: '50px',  ty: '-40px', sc: 1.2  },
    ];
    blobs.forEach(b => {
      const el = document.createElement('div');
      el.className = 'ks-blob';
      el.style.cssText = `width:${b.w}px;height:${b.h}px;left:${b.l};top:${b.t};background:${b.color};--dur:${b.dur};--tx:${b.tx};--ty:${b.ty};--sc:${b.sc}`;
      hero.prepend(el);
    });
    // Add vignette
    hero.classList.add('ks-hero-vignette');
  }

  /* ── 5. Hero mouse spotlight ── */
  function initHeroSpotlight() {
    if (isTouch) return;
    const hero = document.querySelector('.hero-left')?.closest('section')
              || document.querySelector('#hero');
    if (!hero) return;
    hero.classList.add('ks-hero-spotlight');
    hero.addEventListener('mousemove', e => {
      const rect = hero.getBoundingClientRect();
      hero.style.setProperty('--hx', (e.clientX - rect.left) + 'px');
      hero.style.setProperty('--hy', (e.clientY - rect.top)  + 'px');
      hero.classList.add('ks-spotlight-on');
    }, { passive: true });
    hero.addEventListener('mouseleave', () => hero.classList.remove('ks-spotlight-on'));
  }

  /* ── 6. Hero particles ── */
  function initHeroParticles() {
    const hero = document.querySelector('.hero-left')?.closest('section')
              || document.querySelector('#hero');
    if (!hero) return;
    const wrap = document.createElement('div');
    wrap.id = 'ks-particles'; wrap.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 22; i++) {
      const p = document.createElement('span');
      const sz = 1.2 + Math.random() * 2.5;
      p.className = 'ks-particle';
      p.style.cssText = `left:${Math.random()*100}%;top:${Math.random()*100}%;width:${sz.toFixed(1)}px;height:${sz.toFixed(1)}px;--dur:${(4+Math.random()*7).toFixed(1)}s;--delay:${(Math.random()*-10).toFixed(1)}s;--tx:${(-25+Math.random()*50).toFixed(0)}px;--ty:${(-55+Math.random()*30).toFixed(0)}px`;
      wrap.appendChild(p);
    }
    hero.prepend(wrap);
  }

  /* ── 7. Mouse parallax depth layers ── */
  function initMouseParallax() {
    if (isTouch) return;
    const hero = document.querySelector('.hero-left')?.closest('section') || document.querySelector('#hero');
    if (!hero) return;
    const layers = [
      { sel: '.hero-eyebrow', d: 0.012 }, { sel: '.hero-sub',  d: 0.009 },
      { sel: '.hero-btns',    d: 0.007 }, { sel: '.hero-tags', d: 0.005 },
    ].map(l => ({ ...l, el: document.querySelector(l.sel) })).filter(l => l.el);
    if (!layers.length) return;

    let mx = 0, my = 0;
    const rx = layers.map(() => 0), ry = layers.map(() => 0);
    hero.addEventListener('mousemove', e => { mx = e.clientX - window.innerWidth/2; my = e.clientY - window.innerHeight/2; }, { passive: true });
    hero.addEventListener('mouseleave', () => { mx = 0; my = 0; });
    setTimeout(() => {
      (function tick() {
        layers.forEach((l, i) => {
          rx[i] += (mx * l.d - rx[i]) * 0.07; ry[i] += (my * l.d - ry[i]) * 0.07;
          l.el.style.transform = `translate(${rx[i].toFixed(2)}px,${ry[i].toFixed(2)}px)`;
        });
        requestAnimationFrame(tick);
      })();
    }, 1500);
  }

  /* ── 8. Word split ── */
  function initWordSplit() {
    document.querySelectorAll('[data-word-split]').forEach(el => {
      el.classList.add('ks-word-split');
      if (el.children.length === 0) {
        const words = el.textContent.trim().split(/\s+/);
        el.innerHTML = words.map((w, i) =>
          `<span class="ks-word"><span class="ks-word-inner" style="transition-delay:${i * 70}ms">${w}</span></span>`
        ).join(' ');
      }
      const obs = makeObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } });
      }, { threshold: 0.2 });
      obs.observe(el);
    });
  }

  /* ── 9. 3D Tilt + Spotlight ── */
  function init3DTilt() {
    document.querySelectorAll('[data-tilt]').forEach(card => {
      card.addEventListener('mousemove', e => {
        const rect = card.getBoundingClientRect();
        const dx = ((e.clientX - rect.left) / rect.width  - 0.5) * 2;
        const dy = ((e.clientY - rect.top)  / rect.height - 0.5) * 2;
        card.style.transform = `perspective(900px) rotateY(${(dx*12).toFixed(2)}deg) rotateX(${(-dy*12).toFixed(2)}deg) scale3d(1.03,1.03,1.03)`;
        card.style.zIndex = '2';
        card.style.setProperty('--sx', (e.clientX - rect.left) + 'px');
        card.style.setProperty('--sy', (e.clientY - rect.top)  + 'px');
      });
      card.addEventListener('mouseleave', () => { card.style.transform = ''; card.style.zIndex = ''; });
    });
  }

  /* ── 10. Color glow ── */
  function initColorGlow() {
    document.querySelectorAll('[data-tilt]').forEach(card => {
      const text = card.textContent;
      if      (/light[\s-]?blue/i.test(text))                        card.setAttribute('data-glow', 'blue');
      else if (/\bgreen\b/i.test(text) && !/frankfurt/i.test(text))  card.setAttribute('data-glow', 'green');
      else if (/\bpink\b/i.test(text))                                card.setAttribute('data-glow', 'pink');
      else if (/black|frankfurt/i.test(text))                         card.setAttribute('data-glow', 'dark');
    });
  }

  /* ── 11. Magnetic buttons ── */
  function initMagnetic() {
    document.querySelectorAll('[data-magnetic]').forEach(btn => {
      btn.addEventListener('mousemove', e => {
        const rect = btn.getBoundingClientRect();
        btn.style.transform = `translate(${((e.clientX-rect.left-rect.width/2)*0.35).toFixed(1)}px,${((e.clientY-rect.top-rect.height/2)*0.35).toFixed(1)}px)`;
      });
      btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
    });
  }

  /* ── 12. Image reveal ── */
  function initImageReveal() {
    const els = document.querySelectorAll('[data-img-reveal]');
    if (!els.length) return;
    const obs = makeObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });
    els.forEach(el => obs.observe(el));
  }

  /* ── 13. Stat counter with scramble ── */
  function animateCounter(el, target, duration) {
    const scrambleEnd = performance.now() + 520;
    const digits = '0123456789'; const isDecimal = String(target).includes('.');
    function scramble(now) {
      if (now < scrambleEnd) {
        el.textContent = isDecimal ? (Math.random()*target).toFixed(1) : digits[Math.floor(Math.random()*digits.length)];
        requestAnimationFrame(scramble);
      } else {
        const start = performance.now();
        function countUp(ts) {
          const p = Math.min((ts - start) / duration, 1);
          const v = (1 - Math.pow(1-p, 3)) * target;
          el.textContent = isDecimal ? v.toFixed(1) : Math.floor(v);
          if (p < 1) requestAnimationFrame(countUp); else el.textContent = target;
        }
        requestAnimationFrame(countUp);
      }
    }
    requestAnimationFrame(scramble);
  }

  /* ── 14. Typing effect ── */
  function initTyping(el, delay) {
    if (!el) return;
    const text = el.textContent.trim(); el.textContent = '';
    el.classList.add('ks-typing-cursor'); el.style.opacity = '1';
    let i = 0;
    setTimeout(() => {
      const iv = setInterval(() => {
        el.textContent = text.slice(0, ++i);
        if (i >= text.length) { clearInterval(iv); el.classList.remove('ks-typing-cursor'); }
      }, 55);
    }, delay || 400);
  }

  /* ── 15. Text scramble on hover ── */
  const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$';
  function scrambleHover(el) {
    if (el.dataset.ksScrambling) return;
    // For word-split elements, scramble the inner spans
    const innerSpans = el.querySelectorAll('.ks-word-inner');
    if (innerSpans.length) {
      innerSpans.forEach((span, si) => {
        const orig = span.dataset.origText || span.textContent;
        span.dataset.origText = orig;
        let iter = 0;
        setTimeout(() => {
          const iv = setInterval(() => {
            span.textContent = orig.split('').map((c, ci) => {
              if (c === ' ') return ' ';
              return ci < iter / 1.5 ? orig[ci] : SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
            }).join('');
            if (++iter > orig.length * 2) { clearInterval(iv); span.textContent = orig; }
          }, 30);
        }, si * 50);
      });
    } else {
      // Plain text element
      const orig = el.textContent;
      el.dataset.ksScrambling = '1';
      let iter = 0;
      const iv = setInterval(() => {
        el.textContent = orig.split('').map((c, ci) => {
          if (c === ' ') return ' ';
          return ci < iter / 1.8 ? orig[ci] : SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        }).join('');
        if (++iter > orig.length * 2) { clearInterval(iv); el.textContent = orig; delete el.dataset.ksScrambling; }
      }, 28);
    }
  }

  function initTextScramble() {
    // Apply to nav links (very satisfying)
    document.querySelectorAll('.nav-links a').forEach(link => {
      link.addEventListener('mouseenter', () => scrambleHover(link));
    });
    // Apply to section headings
    document.querySelectorAll('.sec-title, .bfe-hdl, .prod-header h2').forEach(el => {
      el.addEventListener('mouseenter', () => scrambleHover(el));
    });
  }

  /* ── 16. Marquee reverses direction on scroll up ── */
  function initMarquee() {
    const anchor = document.getElementById('stats') || document.querySelector('#hero');
    if (!anchor) return;
    const items = [
      'Premium Filter','Kokosnusskohle','Keramikkappe','7mm · 9mm','10er · 50er Pack',
      'Kaero Smoke','Coconut Carbon','Black Frankfurt','Activated Carbon',
      'Premium Filter','Kokosnusskohle','Keramikkappe','7mm · 9mm','10er · 50er Pack',
      'Kaero Smoke','Coconut Carbon','Black Frankfurt','Activated Carbon'
    ];
    const html = items.map(t => `<span class="ks-marquee-item">${t}</span>`).join('');
    const wrap = document.createElement('div');
    wrap.className = 'ks-marquee-wrap'; wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = `<div class="ks-marquee"><div class="ks-marquee-track">${html}</div><div class="ks-marquee-track" aria-hidden="true">${html}</div></div>`;
    anchor.insertAdjacentElement('afterend', wrap);
    /* immer nach links — keine Richtungsumkehr beim Scrollen */
  }

  /* ── 17. Floating hero image ── */
  function initFloating() {
    const img = document.querySelector('.hero-right img, .hero-right picture, .hero-right .product-img');
    if (img) setTimeout(() => img.classList.add('ks-float'), 900);
  }

  /* ── 18. Click ripple ── */
  function initRipple() {
    const SEL = 'button:not(#nav-ham):not(#nav-cart):not(.cart-close):not(#ks-btt), .btn, [role="button"]';
    document.addEventListener('click', e => {
      const btn = e.target.closest(SEL);
      if (!btn) return;
      btn.classList.add('ks-ripple-host');
      const rect = btn.getBoundingClientRect();
      const rpl  = document.createElement('span');
      rpl.className = 'ks-ripple';
      rpl.style.left = (e.clientX - rect.left) + 'px'; rpl.style.top = (e.clientY - rect.top) + 'px';
      btn.appendChild(rpl); setTimeout(() => rpl.remove(), 700);
    });
  }

  /* ── 19. Back to top ── */
  function initBackToTop() {
    const btn = document.createElement('button');
    btn.id = 'ks-btt'; btn.setAttribute('aria-label', 'Nach oben');
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 12V4M4 8l4-4 4 4"/></svg>';
    document.body.appendChild(btn);
    let t = false;
    window.addEventListener('scroll', () => {
      if (!t) { requestAnimationFrame(() => { btn.classList.toggle('ks-btt-visible', window.scrollY > 500); t = false; }); t = true; }
    }, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ── 20. Section navigation dots ── */
  function initSectionDots() {
    const LABELS = { hero:'Start', stats:'Zahlen', products:'Produkte', features:'Vorteile', 'bfe-hero':'Frankfurt', gallery:'Galerie', 'filter-section':'Filter', editions:'Kollektion', 'neon-brand':'Kaero', how:'Ablauf', about:'Über uns', contact:'Kontakt' };
    const sections = Array.from(document.querySelectorAll('section[id]'));
    if (sections.length < 3) return;
    const nav = document.createElement('nav');
    nav.id = 'ks-dots'; nav.setAttribute('aria-label', 'Seitennavigation');
    const dots = sections.map(sec => {
      const dot = document.createElement('button');
      dot.className = 'ks-dot'; dot.setAttribute('data-label', LABELS[sec.id] || sec.id);
      dot.setAttribute('aria-label', 'Zu ' + (LABELS[sec.id] || sec.id));
      dot.addEventListener('click', () => sec.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      nav.appendChild(dot); return { dot, sec };
    });
    document.body.appendChild(nav);
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        const found = dots.find(d => d.sec === e.target);
        if (found) found.dot.classList.toggle('ks-dot-active', e.isIntersecting);
      });
    }, { threshold: 0.5 });
    sections.forEach(s => obs.observe(s));
  }

  /* ── 21. Smooth anchor scrolling ── */
  function initSmoothAnchors() {
    document.addEventListener('click', e => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const target = document.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      document.getElementById('nav-drawer')?.classList.remove('open');
    });
  }

  /* ── 22. Cursor trail (glowing dots chase the cursor) ── */
  function initCursorTrail() {
    if (isTouch) return;
    const NUM = 9;
    const trail = Array.from({ length: NUM }, (_, i) => {
      const d = document.createElement('div');
      d.className = 'ks-trail-dot';
      const sz = Math.max(3, 8 - i * 0.7);
      d.style.cssText = `width:${sz.toFixed(1)}px;height:${sz.toFixed(1)}px;opacity:${(0.72 - i * 0.07).toFixed(2)}`;
      document.body.appendChild(d);
      return d;
    });
    const pos = Array.from({ length: NUM }, () => ({ x: -200, y: -200 }));
    let mx = -200, my = -200;
    document.addEventListener('mousemove', e => { mx = e.clientX; my = e.clientY; }, { passive: true });
    (function tick() {
      pos[0].x += (mx - pos[0].x) * 0.28;
      pos[0].y += (my - pos[0].y) * 0.28;
      for (let i = 1; i < NUM; i++) {
        pos[i].x += (pos[i-1].x - pos[i].x) * 0.38;
        pos[i].y += (pos[i-1].y - pos[i].y) * 0.38;
      }
      trail.forEach((d, i) => {
        d.style.left = pos[i].x.toFixed(1) + 'px';
        d.style.top  = pos[i].y.toFixed(1) + 'px';
        d.style.transform = 'translate(-50%,-50%) scale(1)';
      });
      requestAnimationFrame(tick);
    })();
  }

  /* ── 23. 3D alternating stagger observer ── */
  function initStagger3D() {
    const containers = document.querySelectorAll('[data-stagger-3d]');
    if (!containers.length) return;
    const obs = makeObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    containers.forEach(c => Array.from(c.children).forEach(child => obs.observe(child)));
  }

  /* ── 24. Decorative section numbers ── */
  function initSectionNumbers() {
    if (isTouch) return;
    const MAP = [
      { id: 'products',       num: '01' },
      { id: 'features',       num: '02' },
      { id: 'bfe-hero',       num: '03' },
      { id: 'filter-section', num: '04' },
      { id: 'about',          num: '05' },
      { id: 'contact',        num: '06' },
    ];
    MAP.forEach(({ id, num }) => {
      const sec = document.getElementById(id);
      if (!sec) return;
      if (getComputedStyle(sec).position === 'static') sec.style.position = 'relative';
      const el = document.createElement('div');
      el.className = 'ks-section-num';
      el.textContent = num;
      sec.prepend(el);
      const obs = makeObserver(entries => {
        entries.forEach(e => { if (e.isIntersecting) { el.classList.add('is-visible'); obs.unobserve(sec); } });
      }, { threshold: 0.1 });
      obs.observe(sec);
    });
  }

  /* ── 25. Product card auto-shimmer ── */
  function initCardShimmer() {
    const cards = Array.from(document.querySelectorAll('.prod-card'));
    if (!cards.length) return;
    let idx = 0;
    function shineNext() {
      const card = cards[idx % cards.length];
      idx++;
      if (card.classList.contains('ks-shimmer-active')) return;
      card.classList.add('ks-shimmer-active');
      setTimeout(() => card.classList.remove('ks-shimmer-active'), 850);
    }
    // Stagger initial shine
    cards.forEach((_, i) => setTimeout(() => shineNext(), i * 700 + 2000));
    // Then repeat
    setInterval(() => shineNext(), 2800);
  }

  /* ── 26. Scroll velocity body skew ── */
  function initScrollVelocity() {
    if (isTouch) return;
    let lastY = window.scrollY, rafPending = false, resetTimer;
    window.addEventListener('scroll', () => {
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(() => {
          const vel = window.scrollY - lastY;
          lastY = window.scrollY;
          const skew = Math.max(-2.2, Math.min(2.2, vel * 0.12));
          document.body.style.transform = `skewY(${skew.toFixed(2)}deg)`;
          clearTimeout(resetTimer);
          resetTimer = setTimeout(() => { document.body.style.transform = 'skewY(0deg)'; }, 130);
          rafPending = false;
        });
      }
    }, { passive: true });
  }

  /* ── 27. Heading underline draw ── */
  function initHeadingUnderlines() {
    document.querySelectorAll('.sec-title, .prod-header h2, .bfe-hdl').forEach(el => {
      const line = document.createElement('span');
      line.className = 'ks-hdl-underline';
      el.insertAdjacentElement('afterend', line);
      const obs = makeObserver(entries => {
        entries.forEach(e => { if (e.isIntersecting) { line.classList.add('is-visible'); obs.unobserve(line); } });
      }, { threshold: 0.8 });
      obs.observe(line);
    });
  }

  /* ── 28a. Gallery tab-switch — no thumbs, nothing to animate ── */
  function initGalleryTabWipe() { /* thumbs removed — setGalImg handles main image fade */ }

  /* ── 28b. Gallery hover focus ── */
  function initGalleryFocus() {
    if (isTouch) return;
    const mainImg = document.querySelector('.gal-main');
    if (!mainImg) return;
    // Walk up to find the gallery wrapper
    let wrap = mainImg.parentElement;
    while (wrap && !wrap.querySelector('.gal-thumb')) wrap = wrap.parentElement;
    if (!wrap) return;

    const items = Array.from(wrap.querySelectorAll('.gal-main, .gal-thumb'));
    items.forEach(item => {
      item.classList.add('ks-gal-item');
      item.addEventListener('mouseenter', () => {
        items.forEach(o => {
          o.classList.remove('ks-gal-focus', 'ks-gal-dim');
          if (o === item) o.classList.add('ks-gal-focus');
          else            o.classList.add('ks-gal-dim');
        });
      });
      item.addEventListener('mouseleave', () => {
        items.forEach(o => o.classList.remove('ks-gal-focus', 'ks-gal-dim'));
      });
    });
  }

  /* ── 29. Step connector line ── */
  function initStepLine() {
    const stepsSection = document.querySelector('#filter-section, .steps-section, [id*="step"], [id*="schritt"]')
      || Array.from(document.querySelectorAll('section')).find(s => s.textContent.includes('Schritten'));
    if (!stepsSection) return;
    stepsSection.classList.add('ks-step-line-wrap');
    const obs = makeObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { stepsSection.classList.add('ks-line-visible'); obs.unobserve(stepsSection); } });
    }, { threshold: 0.15 });
    obs.observe(stepsSection);
  }

  /* ── 30. Announcement bar hides on scroll, reappears on scroll-up ── */
  function initAnnounceHide() {
    const ann = document.getElementById('announce');
    if (!ann) return;
    let lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const hide = window.scrollY > 180 && window.scrollY > lastY;
      ann.classList.toggle('ks-ann-hide', hide);
      lastY = window.scrollY;
    }, { passive: true });
  }

  /* ── floating random delays on tags ── */
  function initTagFloat() {
    const tags = document.querySelectorAll('.hero-tags > *, .bfe-badge');
    tags.forEach((t, i) => {
      t.style.setProperty('--tag-dur',   (3.2 + i * 0.55).toFixed(2) + 's');
      t.style.setProperty('--tag-delay', (i   * 0.38).toFixed(2) + 's');
    });
  }

  /* ── About section scroll-parallax split ── */
  function initAboutParallax() {
    if (isTouch) return;
    const text = document.querySelector('#about .about-text');
    const img  = document.querySelector('#about .about-img');
    if (!text || !img) return;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const sec = document.getElementById('about');
        if (!sec) { ticking = false; return; }
        const rect   = sec.getBoundingClientRect();
        const center = rect.top + rect.height / 2 - window.innerHeight / 2;
        text.style.transform = `translateY(${(center * 0.06).toFixed(1)}px)`;
        img.style.transform  = `translateY(${(center * -0.06).toFixed(1)}px)`;
        ticking = false;
      });
    }, { passive: true });
  }

  /* ══════════════════════════════
     ÜBER UNS  — dramatic reveal
     ══════════════════════════════ */
  function initAboutSection() {
    const sec  = document.getElementById('about');
    if (!sec) return;

    // 1. Image: develops from grayscale → full colour + descale
    const imgWrap = sec.querySelector('.about-img');
    if (imgWrap) {
      imgWrap.classList.add('ks-about-img-wrap');
      const obs = makeObserver(entries => {
        entries.forEach(e => {
          if (!e.isIntersecting) return;
          imgWrap.classList.add('ks-about-img-revealed');
          obs.unobserve(imgWrap);
        });
      }, { threshold: 0.25 });
      obs.observe(imgWrap);
    }

    // 2. Text: split each paragraph into lines, reveal line by line
    const textWrap = sec.querySelector('.about-text');
    if (textWrap) {
      // Wrap every <p> in a clip container so lines slide up
      textWrap.querySelectorAll('p, h3, h4, li').forEach((el, i) => {
        el.classList.add('ks-about-line');
        el.style.transitionDelay = (i * 110 + 300) + 'ms';
      });
      textWrap.classList.add('ks-about-text-wrap');
      const obs = makeObserver(entries => {
        entries.forEach(e => {
          if (!e.isIntersecting) return;
          textWrap.classList.add('ks-about-text-on');
          obs.unobserve(textWrap);
        });
      }, { threshold: 0.2 });
      obs.observe(textWrap);
    }

    // 3. Big decorative quote mark behind text
    if (textWrap) {
      const quote = document.createElement('div');
      quote.className = 'ks-big-quote';
      quote.textContent = '„';
      quote.setAttribute('aria-hidden', 'true');
      textWrap.style.position = 'relative';
      textWrap.prepend(quote);
      const obs2 = makeObserver(entries => {
        entries.forEach(e => { if (e.isIntersecting) { quote.classList.add('ks-big-quote-on'); obs2.unobserve(textWrap); } });
      }, { threshold: 0.15 });
      obs2.observe(textWrap);
    }
  }

  /* ════════════════════════════════════════
     WARUM KAERO  — power-on card sequence
     ════════════════════════════════════════ */
  function initFeaturesSection() {
    const grid = document.querySelector('.feat-grid');
    if (!grid) return;

    Array.from(grid.children).forEach((card, i) => {
      // Top accent bar per card
      const bar = document.createElement('div');
      bar.className = 'ks-feat-bar';
      // colour cycles through brand palette
      const colours = ['#5B8DEF','#9B6DFF','#E94080','#5B8DEF'];
      bar.style.setProperty('--bar-color', colours[i % colours.length]);
      card.style.position = 'relative';
      card.style.overflow = 'hidden';
      card.prepend(bar);

      // Observe each card: trigger bar + flash
      const obs = makeObserver(entries => {
        entries.forEach(e => {
          if (!e.isIntersecting) return;
          card.classList.add('ks-feat-powered');
          obs.unobserve(card);
        });
      }, { threshold: 0.3 });
      obs.observe(card);
    });

    // Icon / emoji bounce
    grid.querySelectorAll('.feat-icon, .feat-emoji, [class*="icon"]').forEach((icon, i) => {
      icon.classList.add('ks-feat-icon');
      icon.style.transitionDelay = (i * 120 + 400) + 'ms';
    });
  }

  /* ── Stat number scale-pop at count end ── */
  function patchStatPop() {
    const statItems = document.querySelectorAll('#stats .stat-item, #stats .stat-cell');
    const obs = makeObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const numEl = e.target.querySelector('.stat-n');
        if (numEl) setTimeout(() => numEl.classList.add('ks-stat-pop'), 1450);
        obs.unobserve(e.target);
      });
    }, { threshold: 0.3 });
    statItems.forEach(c => obs.observe(c));
  }

  /* ── Steps: pop each circle when section scrolls in ── */
  function initStepCircles() {
    const circles = document.querySelectorAll('.step-circle, .how-num, [class*="step-num"]');
    if (!circles.length) return;
    const obs = makeObserver((entries) => {
      entries.forEach((e, i) => {
        if (!e.isIntersecting) return;
        setTimeout(() => e.target.classList.add('in-count'), i * 200);
        obs.unobserve(e.target);
      });
    }, { threshold: 0.5 });
    circles.forEach(c => obs.observe(c));
  }

  /* ── Contact section: trigger ks-contact-on ── */
  function initContactReveal() {
    const sec = document.getElementById('contact');
    if (!sec) return;
    const obs = makeObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { sec.classList.add('ks-contact-on'); obs.unobserve(sec); }
      });
    }, { threshold: 0.12 });
    obs.observe(sec);
  }

  /* ── Gallery: curtain reveal — fires when ~30% of section is in view ── */
  function initGalleryKenBurns() {
    const sec = document.getElementById('gallery');
    if (!sec) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        sec.classList.add('ks-gallery-on');
        obs.unobserve(sec);
      });
    }, { threshold: 0.28, rootMargin: '0px 0px 0px 0px' });
    obs.observe(sec);
  }

  /* ── 29. Animated gradient headlines ── */
  function initGradientHeadlines() {
    // Apply slowly shifting colour gradient to BFE headline + hero headline
    ['.bfe-hdl', '.hero-hdl'].forEach(sel => {
      const el = document.querySelector(sel);
      if (!el) return;
      el.classList.add('ks-gradient-hdl');
    });
  }

  /* ── 30. One-shot section light-beam sweep ── */
  function initSectionBeam() {
    const secs = document.querySelectorAll('section[id]');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const sec = e.target;
        if (sec.dataset.ksBeamed) return;
        sec.dataset.ksBeamed = '1';
        const beam = document.createElement('div');
        beam.className = 'ks-beam';
        // ensure section has position context
        if (getComputedStyle(sec).position === 'static') sec.style.position = 'relative';
        sec.appendChild(beam);
        requestAnimationFrame(() => {
          beam.classList.add('ks-beam-go');
          setTimeout(() => beam.remove(), 1400);
        });
        obs.unobserve(sec);
      });
    }, { threshold: 0.18 });
    secs.forEach(s => obs.observe(s));
  }

  /* ── 31. Hero image slow orbit (replaces simple float) ── */
  function initHeroOrbit() {
    const img = document.querySelector('.hero-right img, .hero-right picture');
    if (!img || isTouch) return;
    img.classList.add('ks-orbit');
  }

  /* ── 28. BFE ambient glow blobs ── */
  function initBFEGlow() {
    const bfe = document.getElementById('bfe-hero');
    if (!bfe) return;
    if (getComputedStyle(bfe).position === 'static') bfe.style.position = 'relative';
    const glows = [
      { w: 480, h: 480, l: '-5%',  t: '10%',  color: 'rgba(30,30,40,0.9)',     dur: '8s'  },
      { w: 360, h: 360, l: '60%',  t: '20%',  color: 'rgba(91,141,239,0.12)',  dur: '11s' },
      { w: 300, h: 300, l: '25%',  t: '50%',  color: 'rgba(155,109,255,0.1)',  dur: '9s'  },
    ];
    glows.forEach(g => {
      const el = document.createElement('div');
      el.className = 'ks-bfe-glow';
      el.style.cssText = `width:${g.w}px;height:${g.h}px;left:${g.l};top:${g.t};background:${g.color};--dur:${g.dur}`;
      bfe.prepend(el);
    });
  }

  /* ══════════════════════════════════════════
     APPLY TO KAERO ELEMENTS
     ══════════════════════════════════════════ */
  function applyKaeroAnimations() {

    /* Hero */
    const heroLeft = document.querySelector('.hero-left');
    if (heroLeft) {
      const eyebrow = heroLeft.querySelector('.hero-eyebrow');
      if (eyebrow && !prefersReduced) initTyping(eyebrow, 500);

      const heroMap = [
        { sel: '.hero-hdl',  anim: 'track-in' },
        { sel: '.hero-sub',  anim: 'fade-up' },
        { sel: '.hero-btns', anim: 'fade-up' },
        { sel: '.hero-tags', anim: 'fade-up' },
      ];
      const heroEls = heroMap.map(m => ({ ...m, el: heroLeft.querySelector(m.sel) })).filter(m => m.el);
      heroEls.forEach(({ el, anim }, i) => { el.setAttribute('data-animate', anim); el.style.transitionDelay = (i * 140) + 'ms'; });
      setTimeout(() => heroEls.forEach(({ el }) => el.classList.add('is-visible')), 500);
    }

    const heroRight = document.querySelector('.hero-right');
    if (heroRight) {
      heroRight.setAttribute('data-animate', 'scale-in');
      heroRight.setAttribute('data-parallax', '0.18');
      setTimeout(() => heroRight.classList.add('is-visible'), 600);
    }

    /* Tilt + magnetic */
    document.querySelectorAll('.prod-card').forEach(c => { if (!c.hasAttribute('data-tilt')) c.setAttribute('data-tilt', ''); });
    document.querySelectorAll('.hero-btns a, .hero-btns button, .hero-btns .btn').forEach(b => { if (!b.hasAttribute('data-magnetic')) b.setAttribute('data-magnetic', ''); });

    /* Stats */
    const statCells = document.querySelectorAll('#stats .stat-item, #stats .stat-cell');
    statCells.forEach((cell, i) => { if (!cell.hasAttribute('data-animate')) cell.setAttribute('data-animate', 'stat-card'); });
    const statObs = makeObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const numEl = e.target.querySelector('.stat-n[data-t]');
        if (numEl) { animateCounter(numEl, parseFloat(numEl.dataset.t), 1400); setTimeout(() => e.target.classList.add('ks-stat-glow'), 1500); }
        statObs.unobserve(e.target);
      });
    }, { threshold: 0.3 });
    statCells.forEach(c => statObs.observe(c));

    /* Section headings — word split */
    document.querySelectorAll('.sec-title, .prod-header h2').forEach(el => {
      if (!el.hasAttribute('data-word-split') && !el.hasAttribute('data-animate'))
        el.setAttribute(el.children.length === 0 ? 'data-word-split' : 'data-animate', el.children.length === 0 ? '' : 'reveal');
    });

    const bfeHdl = document.querySelector('#bfe-hero .bfe-hdl');
    if (bfeHdl && !bfeHdl.hasAttribute('data-word-split') && !bfeHdl.hasAttribute('data-animate'))
      bfeHdl.setAttribute(bfeHdl.children.length === 0 ? 'data-word-split' : 'data-animate', 'reveal');

    document.querySelectorAll('.sec-sub, .bfe-desc, .prod-header p').forEach(el => {
      if (!el.hasAttribute('data-animate')) { el.setAttribute('data-animate', 'blur-up'); el.style.transitionDelay = '200ms'; }
    });

    /* Grids — prod-grid: alle 4 Karten gleichzeitig (kein gestaffelter Observer) */
    const prodGrid = document.querySelector('#products .prod-grid');
    if (prodGrid) {
      const pgObs = makeObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            Array.from(e.target.children).forEach(child => child.classList.add('is-visible'));
            pgObs.unobserve(e.target);
          }
        });
      }, { threshold: 0.05, rootMargin: '0px 0px -20px 0px' });
      pgObs.observe(prodGrid);
    }
    // Feature grid gets 3D alternating entrance
    const featGrid = document.querySelector('.feat-grid');
    if (featGrid && !featGrid.hasAttribute('data-stagger-3d') && !featGrid.hasAttribute('data-stagger'))
      featGrid.setAttribute('data-stagger-3d', '');

    /* BFE — data-animate="bfe-img" already set in HTML, no extra stagger needed */

    /* Gallery — ks-gallery-on handles the dramatic entrance, no fade-up override */
    // gal-main intentionally has NO data-animate — initGalleryKenBurns triggers via #gallery section class
    const galThumbs = document.getElementById('gal-thumbs');
    if (galThumbs && !galThumbs.hasAttribute('data-stagger')) galThumbs.setAttribute('data-stagger', '');

    document.querySelectorAll('#bfe-hero .bfe-badge').forEach((el, i) => {
      if (!el.hasAttribute('data-animate') && !el.hasAttribute('data-word-split')) { el.setAttribute('data-animate', 'blur-up'); el.style.transitionDelay = (i*130)+'ms'; }
    });

    const edScroll = document.querySelector('.edition-scroll');
    if (edScroll && !edScroll.hasAttribute('data-stagger')) edScroll.setAttribute('data-stagger', '');

    const filterSec = document.querySelector('#filter-section');
    if (filterSec) {
      filterSec.querySelectorAll('h2, p, .filter-img, .filter-cta').forEach((el, i) => {
        if (!el.hasAttribute('data-animate') && !el.hasAttribute('data-word-split')) {
          el.setAttribute('data-animate', i % 2 === 0 ? 'fade-right' : 'fade-left');
          el.style.transitionDelay = (i * 100) + 'ms';
        }
      });
    }

    document.querySelectorAll('#about .about-text, #about .about-img').forEach((el, i) => {
      if (!el.hasAttribute('data-animate')) el.setAttribute('data-animate', i === 0 ? 'fade-right' : 'fade-left');
    });
  }

  /* ── Init ── */
  function init() {
    applyKaeroAnimations();
    if (!prefersReduced) {
      initMarquee();
      initWordSplit();
    }
    initGeneric();
    initStagger();
    initImageReveal();
    initParallax();
    initSmoothAnchors();

    if (!prefersReduced) {
      initScrollProgress();
      initNavGlass();
      initHeroBlobs();
      initHeroSpotlight();
      // initHeroParticles(); // deaktiviert – Partikel entfernt
      initMouseParallax();
      init3DTilt();
      initColorGlow();
      initMagnetic();
      initFloating();
      initRipple();
      initBackToTop();
      initSectionDots();
      // ── New dramatic effects ──
      initCursorTrail();
      initStagger3D();
      initSectionNumbers();
      initCardShimmer();
      initHeadingUnderlines();
      initBFEGlow();
      initAboutSection();
      initFeaturesSection();
      initGradientHeadlines();
      initSectionBeam();
      initHeroOrbit();
      initGalleryTabWipe();
      initGalleryFocus();
      initGalleryKenBurns();
      initStepLine();
      initStepCircles();
      initAnnounceHide();
      initTagFloat();
      initAboutParallax();
      initContactReveal();
      patchStatPop();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
